"use client"

import { useState, useEffect, Suspense } from "react"
import { useSearchParams } from "next/navigation"
import { motion, AnimatePresence } from "framer-motion"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Progress } from "@/components/ui/progress"
import { Heart } from "lucide-react"

// 3차원 분류: 리더십(leadership) vs 사교성(sociability) vs 모험심(adventure)
const questions = [
  // 리더십 측정 (5문제)
  {
    id: 1,
    q: "이번 주말 다이빙 투어! 계획을 짤 때 나는?",
    a: { text: "강사님이나 친구가 가자는 대로 따라간다.", leadership: 0, sociability: 1, adventure: 0 },
    b: { text: "숙소, 맛집, 풀장 예약까지 내가 다 알아봐야 직성이 풀린다.", leadership: 1, sociability: 0, adventure: 0 }
  },
  {
    id: 2,
    q: "버디가 핀(오리발)을 차고 물에 들어가려고 낑낑댄다면?",
    a: { text: "뒤에서 조용히 지켜보다가 도움을 요청하면 도와준다.", leadership: 0, sociability: 0, adventure: 0 },
    b: { text: "당장 달려가서 '여기 잡으세요!' 하고 신겨준다.", leadership: 1, sociability: 1, adventure: 0 }
  },
  {
    id: 3,
    q: "투어 중 누군가 길을 잃은 것 같다면?",
    a: { text: "다들 멈춰서 같이 기다리자고 제안한다.", leadership: 1, sociability: 1, adventure: 0 },
    b: { text: "강사님이 알아서 하겠지... 나는 조용히 기다린다.", leadership: 0, sociability: 0, adventure: 0 }
  },
  {
    id: 4,
    q: "다이빙 도중 예상치 못한 조류(물살)를 만났다!",
    a: { text: "무서워... 일단 버디나 강사님 옆에 딱 붙는다.", leadership: 0, sociability: 0, adventure: 0 },
    b: { text: "침착해! 내가 앞장설 테니 내 핀만 보고 따라와.", leadership: 1, sociability: 1, adventure: 0 }
  },
  {
    id: 5,
    q: "다이빙이 끝나고 뒷풀이 메뉴를 정할 때?",
    a: { text: "아무거나 다 좋아요~", leadership: 0, sociability: 1, adventure: 0 },
    b: { text: "이 근처에 고기 맛집 있는데 거기로 가시죠.", leadership: 1, sociability: 1, adventure: 0 }
  },

  // 사교성 측정 (5문제)
  {
    id: 6,
    q: "다이빙 후 단체 사진을 찍을 때 나는?",
    a: { text: "자연스럽게 가운데 서서 브이를 그린다.", leadership: 0, sociability: 1, adventure: 0 },
    b: { text: "조용히 구석에 서서 은근슬쩍 빠진다.", leadership: 0, sociability: 0, adventure: 0 }
  },
  {
    id: 7,
    q: "처음 보는 다이버들과 투어를 가게 됐다! 나의 반응은?",
    a: { text: "설레! 새로운 친구 사귈 수 있겠네!", leadership: 0, sociability: 1, adventure: 0 },
    b: { text: "어색한데... 일찍 끝나면 좋겠다.", leadership: 0, sociability: 0, adventure: 0 }
  },
  {
    id: 8,
    q: "물속에서 예쁜 물고기를 발견했을 때 나는?",
    a: { text: "우와! 하면서 버디들에게 손짓으로 알린다.", leadership: 0, sociability: 1, adventure: 0 },
    b: { text: "조용히 혼자 감상하고 올라와서 얘기한다.", leadership: 0, sociability: 0, adventure: 0 }
  },
  {
    id: 9,
    q: "물속에서 멋진 인생샷 스팟을 발견했다! 나의 행동은?",
    a: { text: "내가 저기 가서 설 테니까 예쁘게 찍어줘!", leadership: 0, sociability: 1, adventure: 0 },
    b: { text: "오, 저기 조명 좋다. 버디야 저기 가서 서봐!", leadership: 1, sociability: 0, adventure: 0 }
  },
  {
    id: 10,
    q: "다이빙 후 휴식 시간, 나는?",
    a: { text: "버디들이랑 수다 떨면서 사진 보고 웃는다.", leadership: 0, sociability: 1, adventure: 0 },
    b: { text: "혼자 조용히 앉아서 바다 바라보며 멍때린다.", leadership: 0, sociability: 0, adventure: 0 }
  },

  // 모험심 측정 (5문제)
  {
    id: 11,
    q: "30m 깊이까지 프리다이빙 체험 기회가 생겼다!",
    a: { text: "무서워... 10m만 해도 되지 않을까?", leadership: 0, sociability: 0, adventure: 0 },
    b: { text: "오! 완전 해보고 싶었어! 바로 도전!", leadership: 0, sociability: 0, adventure: 2 }
  },
  {
    id: 12,
    q: "강사님이 동굴 다이빙을 제안했다. 나의 선택은?",
    a: { text: "동굴은 좀... 안전한 곳에서 할게요.", leadership: 0, sociability: 0, adventure: 0 },
    b: { text: "신기할 것 같은데? 조심하면서 한번 해볼까?", leadership: 0, sociability: 0, adventure: 1 }
  },
  {
    id: 13,
    q: "물속에서 처음 보는 대형 물고기가 다가왔다!",
    a: { text: "놀라서 뒤로 물러난다. 무서운데?", leadership: 0, sociability: 0, adventure: 0 },
    b: { text: "우와! 신기하다! 조심스럽게 더 가까이 가본다.", leadership: 0, sociability: 0, adventure: 2 }
  },
  {
    id: 14,
    q: "야간 다이빙 vs 평범한 낮 다이빙, 어떤 게 더 끌려?",
    a: { text: "야간은 무섭잖아... 낮에 밝을 때 하는 게 좋지.", leadership: 0, sociability: 0, adventure: 0 },
    b: { text: "야간 다이빙! 낮과 다른 느낌일 것 같아서 해보고 싶어.", leadership: 0, sociability: 0, adventure: 2 }
  },
  {
    id: 15,
    q: "새로운 다이빙 스팟을 발견했다는 소식! 나의 반응은?",
    a: { text: "검증된 곳에서 안전하게 다이빙하는 게 최고지.", leadership: 0, sociability: 0, adventure: 0 },
    b: { text: "아직 아무도 안 가본 곳? 완전 가고 싶은데!", leadership: 0, sociability: 0, adventure: 2 }
  }
]

const results = {
  // 낮은 리더십 + 낮은 사교성 (내향적 팔로워)
  shy_crab: {
    type: "소심한 작은 게 🦀",
    tag: "#안전제일 #관찰자 #신중파",
    desc: "조용히 자기만의 길을 가는 당신은 안전하고 검증된 것을 선호합니다. 혼자 있는 시간이 편하고, 급하게 결정하는 것보다 천천히 생각하며 다이빙하는 것을 좋아해요.",
    match: "🔥 환상의 짝꿍: 사교적인 바닷가재 🦞",
    cta: "💙 나를 이해해줄 든든한 버디 찾기",
    color: "from-orange-200 to-red-200"
  },
  mysterious_octopus: {
    type: "신비로운 문어 🐙",
    tag: "#독립적 #신중한탐험가 #나만의속도",
    desc: "혼자서도 잘하는 독립적인 당신! 새로운 것도 도전해보고 싶지만, 내 페이스대로 천천히 탐험하는 것을 선호합니다. 조용히 자기만의 세계에서 즐기는 타입이에요.",
    match: "🔥 환상의 짝꿍: 든든한 큰돌고래 🐬",
    cta: "💙 나를 존중해줄 버디 찾기",
    color: "from-purple-200 to-indigo-200"
  },
  free_shark: {
    type: "자유로운 청새리상어 🦈",
    tag: "#자유영혼 #무한도전 #혼자가좋아",
    desc: "누구의 간섭도 받지 않고 자유롭게 물속을 누비는 모험가! 혼자서도 과감하게 도전하며, 규칙이나 제약보다는 본능에 따라 움직이는 것을 좋아합니다.",
    match: "🔥 환상의 짝꿍: 카리스마 대장 범고래 🐋",
    cta: "💙 나와 함께 모험할 버디 찾기",
    color: "from-gray-300 to-slate-400"
  },

  // 낮은 리더십 + 높은 사교성 (외향적 팔로워)
  baby_whale: {
    type: "응석받이 아기고래 🐳",
    tag: "#보호본능자극 #프로팔로워 #챙겨주세요",
    desc: "당신은 누군가의 케어가 필요한 귀여운 아기고래 타입! 혼자 결정하기보단 믿음직한 리더가 이끌어줄 때 가장 편안함을 느낍니다. 물속에서도 저를 지켜주세요!",
    match: "🔥 환상의 짝꿍: 카리스마 대장 범고래 🐋",
    cta: "💙 나를 지켜줄 든든한 버디 찾기",
    color: "from-blue-200 to-blue-400"
  },
  pink_dolphin: {
    type: "인싸력 만렙 핑크돌고래 🐬",
    tag: "#분위기메이커 #리액션부자 #사진맛집",
    desc: "물속에서도 에너지가 넘치는 당신! 버디의 사진도 열심히 찍어주고, 투어 분위기를 띄우는 사랑스러운 분위기 메이커입니다. 당신과 함께라면 지루할 틈이 없어요.",
    match: "🔥 환상의 짝꿍: 평화주의자 바다거북 🐢",
    cta: "💙 나와 텐션 맞는 유쾌한 버디 찾기",
    color: "from-pink-200 to-pink-400"
  },
  playful_seal: {
    type: "에너지 넘치는 물개 🦭",
    tag: "#모험왕 #분위기UP #도전정신",
    desc: "누구보다 활발하고 에너지 넘치는 당신! 사람들과 어울리는 것도 좋아하고, 새로운 도전도 마다하지 않는 열정 넘치는 타입입니다. 항상 웃음소리가 끊이지 않아요!",
    match: "🔥 환상의 짝꿍: 전략가 대왕오징어 🦑",
    cta: "💙 나와 함께 신나게 놀 버디 찾기",
    color: "from-yellow-200 to-amber-300"
  },

  // 높은 리더십 + 낮은 사교성 (내향적 리더)
  sea_turtle: {
    type: "평화주의자 바다거북 🐢",
    tag: "#물아일체 #혼자가편해 #안전제일",
    desc: "느긋하게 물속 세상을 즐기는 평화주의자! 굳이 말하지 않아도 통하는 센스 있는 버디를 선호합니다. 화려한 기술보다 여유로운 유영을 사랑하는 타입이군요.",
    match: "🔥 환상의 짝꿍: 인싸력 만렙 핑크돌고래 🐬",
    cta: "💙 말 없이도 통하는 소울메이트 찾기",
    color: "from-green-200 to-teal-400"
  },
  giant_squid: {
    type: "전략가 대왕오징어 🦑",
    tag: "#침착함 #전략적 #프로탐험가",
    desc: "조용하지만 강력한 리더십을 가진 당신! 상황을 빠르게 파악하고 전략적으로 움직이며, 말보다는 행동으로 보여주는 것을 선호합니다. 위기 상황에서 빛나는 타입!",
    match: "🔥 환상의 짝꿍: 에너지 넘치는 물개 🦭",
    cta: "💙 나와 함께 탐험할 버디 찾기",
    color: "from-red-300 to-orange-300"
  },
  blue_whale: {
    type: "고독한 대왕고래 🐋",
    tag: "#고독한강자 #끝없는도전 #자기주도",
    desc: "세상에서 가장 거대한 고래처럼, 당신은 혼자서도 거대한 바다를 누빌 수 있는 강인함을 가졌습니다. 새로운 도전을 두려워하지 않으며, 자신만의 길을 개척해 나갑니다.",
    match: "🔥 환상의 짝꿍: 자유로운 청새리상어 🦈",
    cta: "💙 나와 함께 깊은 바다로 갈 버디 찾기",
    color: "from-blue-300 to-indigo-400"
  },

  // 높은 리더십 + 높은 사교성 (외향적 리더)
  social_lobster: {
    type: "사교적인 바닷가재 🦞",
    tag: "#사교왕 #책임감 #다정한리더",
    desc: "사람들과 함께하는 것을 좋아하면서도 리더십이 있는 당신! 모두가 즐겁게 다이빙할 수 있도록 챙기고, 분위기도 띄우면서 안전도 책임지는 따뜻한 리더입니다.",
    match: "🔥 환상의 짝꿍: 소심한 작은 게 🦀",
    cta: "💙 나와 함께 즐거운 시간 보낼 버디 찾기",
    color: "from-red-200 to-pink-300"
  },
  dolphin_leader: {
    type: "든든한 큰돌고래 🐬",
    tag: "#완벽한밸런스 #믿음직함 #팀플레이어",
    desc: "리더십도 있고 친화력도 좋은 완벽한 밸런스의 소유자! 팀을 이끌면서도 모두의 의견을 경청하고, 적절한 도전과 안전 사이에서 균형을 잡는 든든한 버디입니다.",
    match: "🔥 환상의 짝꿍: 신비로운 문어 🐙",
    cta: "💙 나와 함께 성장할 버디 찾기",
    color: "from-cyan-200 to-blue-300"
  },
  orca: {
    type: "카리스마 대장 범고래 🐋",
    tag: "#리더십 #걸크러쉬 #나만믿어",
    desc: "판단력 빠르고 책임감 강한 당신은 타고난 리더! 사람들과 어울리는 것도 좋아하고, 과감한 도전도 마다하지 않습니다. '내 뒤에 딱 붙어!'라고 말할 때 제일 멋있어요.",
    match: "🔥 환상의 짝꿍: 응석받이 아기고래 🐳",
    cta: "💙 내가 리드해줄 귀여운 버디 찾기",
    color: "from-gray-700 to-black"
  }
}

type Screen = "start" | "quiz" | "loading" | "result"
type ResultType = "shy_crab" | "mysterious_octopus" | "free_shark" | "baby_whale" | "pink_dolphin" | "playful_seal" | "sea_turtle" | "giant_squid" | "blue_whale" | "social_lobster" | "dolphin_leader" | "orca"

// 3차원 분류: 리더십 x 사교성 x 모험심 = 2 x 2 x 3 = 12가지
const getResultType = (leadership: number, sociability: number, adventure: number): ResultType => {
  const avgLeadership = leadership / 5 // 0~1 사이 값 (5문제)
  const avgSociability = sociability / 5 // 0~1 사이 값 (5문제)
  const avgAdventure = adventure / 10 // 0~1 사이 값 (5문제, 각 0/1/2점)

  // 모험심 레벨: 낮음(0-0.33), 중간(0.34-0.66), 높음(0.67-1.0)
  const adventureLevel = avgAdventure < 0.34 ? 0 : avgAdventure < 0.67 ? 1 : 2

  // 낮은 리더십 + 낮은 사교성
  if (avgLeadership < 0.5 && avgSociability < 0.5) {
    if (adventureLevel === 0) return "shy_crab"
    if (adventureLevel === 1) return "mysterious_octopus"
    return "free_shark"
  }

  // 낮은 리더십 + 높은 사교성
  if (avgLeadership < 0.5 && avgSociability >= 0.5) {
    if (adventureLevel === 0) return "baby_whale"
    if (adventureLevel === 1) return "pink_dolphin"
    return "playful_seal"
  }

  // 높은 리더십 + 낮은 사교성
  if (avgLeadership >= 0.5 && avgSociability < 0.5) {
    if (adventureLevel === 0) return "sea_turtle"
    if (adventureLevel === 1) return "giant_squid"
    return "blue_whale"
  }

  // 높은 리더십 + 높은 사교성
  if (adventureLevel === 0) return "social_lobster"
  if (adventureLevel === 1) return "dolphin_leader"
  return "orca"
}

// 12가지 결과 동물 이모지 목록
const ALL_EMOJIS = ["🦀", "🐙", "🦈", "🐳", "🐬", "🦭", "🐢", "🦑", "🐋", "🦞", "🐬", "🐋"]

// 시작 화면 배경 이모지 - 부유 효과
const FloatingEmojis = () => {
  const positions = [
    { emoji: "🦀", left: "5%", delay: 0, duration: 8 },
    { emoji: "🐙", left: "15%", delay: 1.2, duration: 7 },
    { emoji: "🦈", left: "25%", delay: 0.5, duration: 9 },
    { emoji: "🐳", left: "38%", delay: 2, duration: 6.5 },
    { emoji: "🐬", left: "52%", delay: 0.8, duration: 8.5 },
    { emoji: "🦭", left: "65%", delay: 1.5, duration: 7.5 },
    { emoji: "🐢", left: "75%", delay: 0.3, duration: 9.5 },
    { emoji: "🦑", left: "85%", delay: 1.8, duration: 6 },
    { emoji: "🐋", left: "93%", delay: 0.7, duration: 8 },
  ]
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {positions.map((p, i) => (
        <motion.div
          key={i}
          className="absolute text-2xl opacity-30"
          style={{ left: p.left, bottom: "-40px" }}
          animate={{ y: [0, -600], opacity: [0, 0.4, 0.4, 0] }}
          transition={{ duration: p.duration, repeat: Infinity, delay: p.delay, ease: "linear" }}
        >
          {p.emoji}
        </motion.div>
      ))}
    </div>
  )
}

// 질문 카테고리 뱃지
const getCategoryBadge = (questionIndex: number) => {
  if (questionIndex < 5) return { label: "리더십 🎯", color: "bg-blue-100 text-blue-700" }
  if (questionIndex < 10) return { label: "사교성 💬", color: "bg-pink-100 text-pink-700" }
  return { label: "모험심 🔥", color: "bg-orange-100 text-orange-700" }
}

// 로딩 분석 텍스트 단계
const ANALYSIS_STEPS = [
  "리더십 분석 중...",
  "사교성 분석 중...",
  "최적의 버디 탐색 중...",
]

function SoulmateTestContent() {
  const searchParams = useSearchParams()
  const [screen, setScreen] = useState<Screen>("start")
  const [currentQuestion, setCurrentQuestion] = useState(0)
  const [leadershipScore, setLeadershipScore] = useState(0)
  const [sociabilityScore, setSociabilityScore] = useState(0)
  const [adventureScore, setAdventureScore] = useState(0)
  const [resultType, setResultType] = useState<ResultType>("pink_dolphin")
  const [copied, setCopied] = useState(false)
  const [analysisStep, setAnalysisStep] = useState(0)

  // URL 파라미터로 결과 페이지 바로 표시
  useEffect(() => {
    const resultParam = searchParams.get('result')
    if (resultParam && resultParam in results) {
      setResultType(resultParam as ResultType)
      setScreen("result")
    }
  }, [searchParams])

  // 로딩 화면 분석 텍스트 순차 표시
  useEffect(() => {
    if (screen === "loading") {
      setAnalysisStep(0)
      const t1 = setTimeout(() => setAnalysisStep(1), 750)
      const t2 = setTimeout(() => setAnalysisStep(2), 1600)
      return () => { clearTimeout(t1); clearTimeout(t2) }
    }
  }, [screen])

  const handleStart = () => {
    setScreen("quiz")
    setCurrentQuestion(0)
    setLeadershipScore(0)
    setSociabilityScore(0)
    setAdventureScore(0)
  }

  const handleAnswer = (choice: 'a' | 'b') => {
    const answer = questions[currentQuestion][choice]
    const newLeadership = leadershipScore + answer.leadership
    const newSociability = sociabilityScore + answer.sociability
    const newAdventure = adventureScore + answer.adventure

    if (currentQuestion < questions.length - 1) {
      setLeadershipScore(newLeadership)
      setSociabilityScore(newSociability)
      setAdventureScore(newAdventure)
      setCurrentQuestion(currentQuestion + 1)
    } else {
      setLeadershipScore(newLeadership)
      setSociabilityScore(newSociability)
      setAdventureScore(newAdventure)
      setScreen("loading")

      const finalType = getResultType(newLeadership, newSociability, newAdventure)
      setResultType(finalType)

      setTimeout(() => {
        setScreen("result")
      }, 2500)
    }
  }

  const handleRestart = () => {
    setScreen("start")
    setCurrentQuestion(0)
    setLeadershipScore(0)
    setSociabilityScore(0)
    setAdventureScore(0)
    setResultType("pink_dolphin")
  }

  const shareToKakao = () => {
    const result = results[resultType]
    const resultUrl = typeof window !== 'undefined' ? `${window.location.origin}/soulmate-test?result=${resultType}` : ''
    const imageUrl = typeof window !== 'undefined' ? `${window.location.origin}/og-image.png` : 'https://todaydiving.vercel.app/og-image.png'

    if (typeof window !== 'undefined' && (window as any).Kakao && (window as any).Kakao.isInitialized()) {
      (window as any).Kakao.Share.sendDefault({
        objectType: 'feed',
        content: {
          title: '💙 물속 내 이상형 찾기',
          description: `${result.type}\n\n${result.desc}`,
          imageUrl: imageUrl,
          link: {
            mobileWebUrl: resultUrl,
            webUrl: resultUrl,
          },
        },
        buttons: [
          {
            title: '내 결과 보기',
            link: {
              mobileWebUrl: resultUrl,
              webUrl: resultUrl,
            },
          },
          {
            title: '나도 테스트하기',
            link: {
              mobileWebUrl: `${window.location.origin}/soulmate-test`,
              webUrl: `${window.location.origin}/soulmate-test`,
            },
          },
        ],
      })
    } else {
      // 카카오 미초기화 시 클립보드 복사
      navigator.clipboard.writeText(resultUrl).then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      }).catch(() => {})
    }
  }

  const shareToInstagram = async () => {
    const resultUrl = typeof window !== 'undefined' ? `${window.location.origin}/soulmate-test?result=${resultType}` : ''

    try {
      await navigator.clipboard.writeText(resultUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      // 복사 실패 시 fallback — URL을 콘솔에만 기록
      console.error('Copy failed:', error)
    }
  }

  const progress = ((currentQuestion + 1) / questions.length) * 100
  const result = results[resultType]

  // 3차원 능력치 퍼센트 (결과 화면)
  const leadershipPct = Math.round((leadershipScore / 5) * 100)
  const sociabilityPct = Math.round((sociabilityScore / 5) * 100)
  const adventurePct = Math.round((adventureScore / 10) * 100)

  const categoryBadge = getCategoryBadge(currentQuestion)

  return (
    <div className="min-h-screen bg-gradient-to-b from-cyan-50 via-pink-50 to-blue-50 flex items-center justify-center p-4 relative">
      <AnimatePresence mode="wait">
        {/* START SCREEN */}
        {screen === "start" && (
          <motion.div
            key="start"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="text-center max-w-2xl w-full relative"
          >
            {/* 12가지 동물 이모지 배경 */}
            <FloatingEmojis />

            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.6 }}
              className="mb-4 relative z-10"
            >
              <div className="flex justify-center items-center mb-3">
                <Heart className="w-16 h-16 text-pink-400" />
              </div>
              <h1 className="text-2xl md:text-5xl font-bold mb-2 bg-gradient-to-r from-cyan-500 to-pink-500 bg-clip-text text-transparent">
                물속 내 이상형 찾기
              </h1>
              <p className="text-sm md:text-lg text-gray-700 mb-4">
                나의 다이빙 성향과 딱 맞는<br className="md:hidden" />
                운명의 버디는 누구?
              </p>
              <div className="flex flex-wrap gap-2 justify-center mb-6">
                <span className="px-3 py-1 bg-cyan-100 text-cyan-700 rounded-full text-xs font-medium">
                  #소울메이트찾기
                </span>
                <span className="px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-xs font-medium">
                  #다이빙버디
                </span>
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                  #운명의짝꿍
                </span>
              </div>
            </motion.div>

            <div className="relative z-10">
              <motion.div
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <Button
                  onClick={handleStart}
                  size="lg"
                  className="bg-gradient-to-r from-cyan-400 to-pink-400 hover:from-cyan-500 hover:to-pink-500 text-white px-8 py-4 text-base rounded-full shadow-lg hover:shadow-xl transition-all"
                >
                  테스트 시작하기 💙
                </Button>
              </motion.div>
            </div>
          </motion.div>
        )}

        {/* QUIZ SCREEN */}
        {screen === "quiz" && (
          <motion.div
            key="quiz"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="w-full max-w-2xl"
          >
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-medium text-gray-600">
                  {currentQuestion + 1} / {questions.length}
                </span>
                <span className="text-xs font-medium text-gray-600">{Math.round(progress)}%</span>
              </div>
              <div className="relative">
                <Progress value={progress} className="h-2 bg-gray-200" />
                <motion.div
                  className="absolute top-0 left-0 h-2 bg-gradient-to-r from-cyan-400 to-pink-400 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </div>

            <Card className="p-4 md:p-6 bg-white/90 backdrop-blur-sm shadow-2xl border-0 rounded-3xl">
              <motion.div
                key={currentQuestion}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
              >
                {/* 카테고리 뱃지 */}
                <div className="mb-3">
                  <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${categoryBadge.color}`}>
                    {categoryBadge.label}
                  </span>
                </div>

                <h2 className="text-base md:text-2xl font-bold mb-3 md:mb-4 text-gray-800 text-balance leading-relaxed">
                  {questions[currentQuestion].q}
                </h2>

                <div className="space-y-2 md:space-y-3">
                  <Button
                    onClick={() => handleAnswer('a')}
                    variant="outline"
                    className="w-full p-3 md:p-4 text-left text-sm md:text-base justify-start hover:bg-gradient-to-r hover:from-cyan-50 hover:to-pink-50 hover:text-gray-900 hover:border-cyan-400 transition-all border-2 rounded-2xl"
                  >
                    <span className="font-semibold mr-1.5 md:mr-2 text-cyan-500">A.</span>
                    <span className="text-pretty leading-snug">{questions[currentQuestion].a.text}</span>
                  </Button>

                  <Button
                    onClick={() => handleAnswer('b')}
                    variant="outline"
                    className="w-full p-3 md:p-4 text-left text-sm md:text-base justify-start hover:bg-gradient-to-r hover:from-pink-50 hover:to-cyan-50 hover:text-gray-900 hover:border-pink-400 transition-all border-2 rounded-2xl"
                  >
                    <span className="font-semibold mr-1.5 md:mr-2 text-pink-500">B.</span>
                    <span className="text-pretty leading-snug">{questions[currentQuestion].b.text}</span>
                  </Button>
                </div>
              </motion.div>
            </Card>
          </motion.div>
        )}

        {/* LOADING SCREEN */}
        {screen === "loading" && (
          <motion.div
            key="loading"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.1 }}
            className="text-center"
          >
            <motion.div className="relative mb-4 h-20 w-48 mx-auto">
              {/* Bubbles animation */}
              {[...Array(5)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-3 h-3 bg-cyan-400/30 rounded-full"
                  style={{
                    left: `${20 + i * 15}%`,
                    bottom: 0,
                  }}
                  animate={{
                    y: [-80, -200],
                    opacity: [0, 1, 0],
                    scale: [1, 1.5, 0.5],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: i * 0.2,
                    ease: "easeOut",
                  }}
                />
              ))}
              <div className="absolute inset-0 flex items-center justify-center">
                <Heart className="w-16 h-16 text-pink-400" />
              </div>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-lg md:text-2xl font-bold bg-gradient-to-r from-cyan-500 to-pink-500 bg-clip-text text-transparent"
            >
              운명의 소울메이트를 찾는 중...
            </motion.p>

            {/* 순차적 분석 텍스트 */}
            <div className="mt-3 h-6">
              <AnimatePresence mode="wait">
                <motion.p
                  key={analysisStep}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.4 }}
                  className="text-sm md:text-base text-gray-600"
                >
                  {ANALYSIS_STEPS[analysisStep]}
                </motion.p>
              </AnimatePresence>
            </div>
          </motion.div>
        )}

        {/* RESULT SCREEN */}
        {screen === "result" && (
          <motion.div
            key="result"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="w-full max-w-2xl"
          >
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-center"
            >
              {/* Result Illustration */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: "spring" }}
                className="text-center mb-6"
              >
                <div className="text-8xl md:text-9xl">{result.type.split(' ').slice(-1)[0]}</div>
              </motion.div>

              <Card className="p-4 md:p-6 mb-4 bg-white/95 backdrop-blur-sm shadow-2xl border-0 rounded-3xl">
                <h2 className="text-xl md:text-3xl font-bold mb-2 md:mb-3 text-gray-800">
                  {result.type}
                </h2>
                <div className="flex flex-wrap gap-1.5 md:gap-2 justify-center mb-3 md:mb-4">
                  {result.tag.split(' ').map((tag, i) => (
                    <span
                      key={i}
                      className="px-2 py-0.5 md:py-1 bg-gradient-to-r from-cyan-100 to-pink-100 text-gray-700 rounded-full text-[10px] md:text-xs font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <p className="text-sm md:text-lg leading-relaxed text-gray-700 mb-3 md:mb-4">
                  {result.desc}
                </p>
                <div className="p-2 md:p-3 bg-gradient-to-r from-pink-50 to-cyan-50 rounded-2xl border-2 border-pink-200">
                  <p className="text-sm md:text-lg font-bold text-pink-600">{result.match}</p>
                </div>

                {/* 3가지 능력치 바 */}
                <div className="mt-4 space-y-3">
                  {[
                    { label: "리더십", pct: leadershipPct, color: "from-blue-400 to-cyan-400" },
                    { label: "사교성", pct: sociabilityPct, color: "from-pink-400 to-rose-400" },
                    { label: "모험심", pct: adventurePct, color: "from-orange-400 to-amber-400" },
                  ].map((stat, i) => (
                    <div key={stat.label}>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-xs font-semibold text-gray-600">{stat.label}</span>
                        <span className="text-xs font-bold text-gray-700">{stat.pct}%</span>
                      </div>
                      <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                        <motion.div
                          className={`h-full rounded-full bg-gradient-to-r ${stat.color}`}
                          initial={{ width: 0 }}
                          animate={{ width: `${stat.pct}%` }}
                          transition={{ duration: 0.8, delay: 0.5 + i * 0.15, ease: "easeOut" }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              <div className="space-y-2 md:space-y-3">
                <Button
                  onClick={() => (window.location.href = "/")}
                  size="lg"
                  className="w-full py-3 md:py-4 text-sm md:text-base rounded-2xl shadow-lg hover:shadow-xl transition-all bg-gradient-to-r from-cyan-400 to-pink-400 hover:from-cyan-500 hover:to-pink-500 text-white"
                >
                  {result.cta}
                </Button>

                {/* 인라인 복사 피드백 */}
                {copied && (
                  <motion.p
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="text-xs font-medium text-center text-cyan-600"
                  >
                    링크 복사됨! ✓
                  </motion.p>
                )}

                {/* Share Buttons */}
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    onClick={shareToKakao}
                    size="lg"
                    className="py-3 md:py-4 text-sm md:text-base rounded-2xl shadow-lg hover:shadow-xl transition-all bg-yellow-400 hover:bg-yellow-500 text-gray-900"
                  >
                    <span className="mr-1">💬</span>
                    카카오톡
                  </Button>
                  <Button
                    onClick={shareToInstagram}
                    size="lg"
                    className="py-3 md:py-4 text-sm md:text-base rounded-2xl shadow-lg hover:shadow-xl transition-all bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 hover:from-purple-600 hover:via-pink-600 hover:to-orange-600 text-white"
                  >
                    <span className="mr-1">📷</span>
                    {copied ? "복사됨! ✓" : "인스타"}
                  </Button>
                </div>

                <Button
                  onClick={handleRestart}
                  variant="outline"
                  size="lg"
                  className="w-full py-4 text-base rounded-2xl transition-all border-2 border-gray-300 hover:border-cyan-400 hover:bg-cyan-50"
                >
                  다시 테스트하기
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default function SoulmateTestPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-gradient-to-b from-cyan-50 via-pink-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Heart className="w-16 h-16 mx-auto mb-4 text-pink-400 animate-pulse" />
          <p className="text-gray-600">로딩 중...</p>
        </div>
      </div>
    }>
      <SoulmateTestContent />
    </Suspense>
  )
}
